// classificationFloorSyncPlugin.js
// Reactively syncs a block's classification level to match its citations whenever any
// reference is added to, removed from, or replaced in Y.Map("referenceMarks") — regardless of
// which code path wrote the change (drag-and-drop, the "Cite this" button, accepting a
// suggested citation, deleting/replacing a citation, deleting a reference mark entirely,
// Forecast import, alternative-citation suggestions, etc.).
//
// A block can never be classified lower than its most-classified citation, so the effective
// level is always manualBaseline ? max(manualBaseline, citationsFloor) : citationsFloor —
// RAISED to match a new/higher citation, and LOWERED back down (but never below manualBaseline,
// or all the way to the citations floor if there is no manualBaseline) when citations shrink.
// manualBaseline is read directly from the block's persisted `manualClassificationLevel` attr
// (null if the user never explicitly chose a level via the Classification menu) — no separate
// cache is needed since the attr is the single source of truth. See
// referenceClassificationFloor.js for the full formula.
//
// Previously this sync was only performed by snippetDropPlugin.js's drag-and-drop handlers, so
// every other way of adding/removing a reference (see referencesYMap.js's call sites) silently
// skipped it. Subscribing to the Y.Map directly — the same pattern useReferenceNumberStore
// already uses for citation numbering — makes the sync a property of "a reference changed"
// rather than a responsibility each call site has to remember.
//
// See referenceClassificationFloor.js for the underlying sync logic and
// documentClassification.js's getBlockCitationsMaxLevel for how the floor is computed.

import {Plugin, PluginKey} from 'prosemirror-state';
import {syncBlockClassificationFloorForReference} from '../helpers/referenceClassificationFloor';

export const classificationFloorSyncPluginKey = new PluginKey('classificationFloorSync');

const DEBUG_CLASSIFICATION_FLOOR_SYNC =
    typeof window !== 'undefined' &&
    (window.__DEBUG_CLASSIFICATION_FLOOR_SYNC__ || localStorage.getItem('DEBUG_CLASSIFICATION_FLOOR_SYNC') === '1');

/**
 * Extract the set of referenceMarkIds touched by a batch of Y.observeDeep events.
 * Mirrors the relevance check in referencesYMap.js's observeReferencesForMark:
 * - `event.path[0]` identifies the mark when the change happened inside a mark's subtree
 *   (addedReferences, claims, _meta, ...) — the common case for addReference/removeReferenceMark.
 * - When the change is on yReferenceMarks itself (`event.path.length === 0`), `keysChanged`
 *   holds the mark ids that were added/removed wholesale.
 *
 * @param {import('yjs').YEvent[]} events
 * @returns {Set<string>}
 */
function collectChangedMarkIds(events) {
    const markIds = new Set();
    events.forEach((event) => {
        if (event.path.length > 0) {
            markIds.add(event.path[0]);
        } else if (event.keysChanged) {
            event.keysChanged.forEach((key) => markIds.add(key));
        }
    });
    return markIds;
}

/**
 * Create the classification-floor-sync plugin.
 *
 * @param {Object} options
 * @param {string} options.documentId - report/document id, for the Firestore header sync
 * @param {import('yjs').Map} options.yReferenceMarks - Y.Map("referenceMarks"). Passed
 *   directly (not read from useYDocStore) because ProseMirror invokes a plugin's `view()`
 *   hook synchronously inside `new EditorView(...)` — i.e. inside createEditorView.js —
 *   which runs BEFORE useEditorSetup.js calls `useYDocStore.getState().initialize(...)`.
 *   Reading from the store here would see it still `null` and silently never subscribe.
 * @returns {Plugin}
 */
export function classificationFloorSyncPlugin(options = {}) {
    const {documentId, yReferenceMarks} = options;

    return new Plugin({
        key: classificationFloorSyncPluginKey,

        view(editorView) {
            if (!yReferenceMarks) return {};

            let destroyed = false;

            const handler = (events) => {
                if (destroyed) return;
                const markIds = collectChangedMarkIds(events);
                if (markIds.size === 0) return;

                if (DEBUG_CLASSIFICATION_FLOOR_SYNC) {
                    console.log('[classificationFloorSync] referenceMarks changed', {markIds: Array.from(markIds)});
                }

                markIds.forEach((markId) => {
                    syncBlockClassificationFloorForReference(editorView, markId, documentId, yReferenceMarks);
                });
            };

            yReferenceMarks.observeDeep(handler);

            return {
                destroy() {
                    destroyed = true;
                    yReferenceMarks.unobserveDeep(handler);
                },
            };
        },
    });
}
